// Bookings by kind, then by date, in one place for the web view's Bookings (and an AI's view of them,
// should it want them), so the kinds and their order can't drift apart (features/bookings-and-costs.md,
// "By kind, one list"). A thing's kind follows its icon and its kind (src/categories.ts): a journey's is its
// mode (a flight, else a way of getting around), a place's its kind, so a row's kind and its icon always
// agree. Plain functions, used by the server and the web view.
import {
  byCategory,
  type Categorized,
  type CategoryIcon,
  type Kind,
  kindOf,
  type Mode,
  modeIn,
  modeOf,
} from "./categories.ts";
import { daySpan } from "./days.ts";
import type { When } from "./model.ts";

// The kinds, in the order they always show in (the founder's pick, 2026-10-23: Flights, then Stays, as they
// named them; trains, buses and shuttles, transfers and taxis, ferries and every other way are Getting
// around, ten groups until then).
export const BOOK_GROUPS = [
  { key: "flights", label: "Flights" },
  { key: "stays", label: "Stays" },
  { key: "around", label: "Getting around" },
  { key: "activities", label: "Tickets and activities" },
  { key: "food", label: "Food and drink" },
  { key: "other", label: "Other" },
] as const;
export type BookGroup = (typeof BOOK_GROUPS)[number]["key"];

// Anything with an item's shape: the server's stored records and the web view's items alike.
export interface Groupable extends Categorized {
  id: string;
  title: string;
  parent?: string | undefined;
  status?: string | undefined;
  when?: When | undefined;
  // A to-do's due date: when it happens, when it isn't for a thing with a time of its own.
  due?: string | undefined;
}

// A way of getting around by its icon (the mode a journey's words say, or a place's category icon): a
// flight, else getting around.
const byIcon = (icon: Mode | CategoryIcon | undefined): BookGroup => (icon === "plane" ? "flights" : "around");

const BY_KIND: Record<Exclude<Kind, "around">, BookGroup> = {
  stays: "stays",
  food: "food",
  sights: "activities",
  nature: "activities",
  shopping: "activities",
  other: "other",
};
const byKind = (kind: Kind, icon: CategoryIcon | undefined): BookGroup =>
  kind === "around" ? byIcon(icon) : BY_KIND[kind];

// What some words say a booking is: a mode first ("Reserve Nest hotel shuttle" is the shuttle, not the
// hotel), then a category's words ("Book USJ tickets").
function byWords(text: string | undefined): BookGroup | null {
  const mode = modeIn(text);
  if (mode) return byIcon(mode);
  const match = byCategory(text);
  return match ? byKind(match.kind, match.icon) : null;
}

// Which kind a thing goes in. A to-do takes the kind of the thing it's for (`find` looks it up), else what
// its own category or title says, else Other. A choice goes by its options (`optionsOf`) when they're all of
// one kind (dinner between two restaurants is food), else a choice of how we get there by its mode and one of
// where we sleep with the stays.
export function bookGroupOf<T extends Groupable>(
  item: T,
  find: (id: string) => T | undefined,
  optionsOf: (id: string) => readonly T[] = () => [],
): BookGroup {
  if (item.type === "task") {
    const parent = item.parent ? find(item.parent) : undefined;
    if (parent && parent.type !== "task") return bookGroupOf(parent, find, optionsOf);
    return byWords(item.category) ?? byWords(item.title) ?? "other";
  }
  if (item.type === "stay") return "stays";
  if (item.type === "move") return byIcon(modeOf(item));
  if (item.type === "choice") {
    const kinds = new Set(optionsOf(item.id).map((o) => bookGroupOf(o, find)));
    const [only] = kinds;
    if (only && kinds.size === 1) return only;
    return item.from ? byIcon(modeOf(item)) : "stays";
  }
  return byKind(kindOf(item), byCategory(item.category)?.icon);
}

export interface Group<R> {
  key: BookGroup;
  label: string;
  rows: R[];
}

// A part of a day, in the day's order (after a time-less day, before nothing in particular).
const PART_AT: Record<string, string> = { morning: "09:00", afternoon: "14:00", evening: "18:00", night: "21:00" };
const timeOf = (when: When | undefined): string => {
  if (!when) return "";
  if (when.precision === "exact" || when.precision === "window") return when.start.slice(11, 16);
  if (when.precision === "part") return PART_AT[when.part] ?? "";
  return "";
};

// Rows (`itemOf` says each row's thing), by kind in BOOK_GROUPS' order (a kind with nothing in it left
// out), each kind in the order things happen: a stay by its first night, a journey by when it leaves, a
// place by its day, a to-do by the thing it's for, else by its due date; then what has no date yet, by
// title. `all`: everything in the trip, to find what a to-do is for; `start`: the trip's first date.
export function bookGroups<R, T extends Groupable>(
  rows: readonly R[],
  itemOf: (row: R) => T,
  all: readonly T[],
  start: string | null,
): Group<R>[] {
  const byId = new Map(all.map((i) => [i.id, i]));
  for (const item of rows.map(itemOf)) if (!byId.has(item.id)) byId.set(item.id, item);
  const find = (id: string) => byId.get(id);
  // A choice's options, but those dropped or called off.
  const optionsOf = (id: string) =>
    all.filter((i) => i.parent === id && i.type !== "task" && i.status !== "dropped" && i.status !== "cancelled");
  // When a thing happens: a to-do's is the thing it's for, when it's for one, else its due date.
  const whenOf = (item: T): When | undefined => {
    if (item.type !== "task") return item.when;
    const parent = item.parent ? find(item.parent) : undefined;
    const due: When | undefined = item.due ? { precision: "day", day: item.due } : undefined;
    return (parent && parent.type !== "task" ? parent.when : undefined) ?? item.when ?? due;
  };
  const placed = rows.map((row) => {
    const item = itemOf(row);
    const when = whenOf(item);
    return { row, item, group: bookGroupOf(item, find, optionsOf), span: daySpan(when, start), time: timeOf(when) };
  });
  type Placed = (typeof placed)[number];
  const order = (a: Placed, b: Placed) => {
    if (a.span && b.span) {
      return a.span.first - b.span.first || a.time.localeCompare(b.time) || a.item.title.localeCompare(b.item.title);
    }
    if (a.span) return -1;
    if (b.span) return 1;
    return a.item.title.localeCompare(b.item.title);
  };
  return BOOK_GROUPS.flatMap(({ key, label }) => {
    const mine = placed.filter((p) => p.group === key).sort(order);
    return mine.length ? [{ key, label, rows: mine.map((p) => p.row) }] : [];
  });
}
